import React, { useState, useEffect } from 'react';
import { useParams } from 'wouter';

import { supabase } from '../utils/supabaseClient';

// Extend the Window interface to include __lastSupabaseUpload
declare global {
  interface Window {
    __lastSupabaseUpload?: string;
  }
}

// --- Simple in-browser static analysis for SQL stored procs ---
function analyzeSQL(sql: string) {
  const observations = [];
  // 1. Oversized Procedure
  const lineCount = sql.split(/\r?\n/).length;
  if (lineCount > 500) {
    observations.push({
      title: 'Oversized Procedure Detected',
      evidence: `Procedure has ${lineCount} lines (recommended <500).`,
      why: 'Large procedures are hard to maintain, debug, and test.',
      recommendation: 'Break down into smaller, modular procedures.'
    });
  }
  // 2. Parameter Bloating
  const paramMatch = sql.match(/CREATE\s+PROCEDURE[\s\S]+?\(([^)]*)\)/i);
  if (paramMatch) {
    const paramCount = paramMatch[1].split(',').length;
    if (paramCount > 15) {
      observations.push({
        title: 'Parameter Bloating Detected',
        evidence: `Procedure has ${paramCount} parameters (recommended <15).`,
        why: 'Too many parameters indicate design issues and create maintenance challenges.',
        recommendation: 'Refactor to use parameter tables or configuration tables.'
      });
    }
  }
  // 3. Missing Error Handling
  if (!/TRY\s*\{/i.test(sql) && !/CATCH\s*\{/i.test(sql) && !/TRY\s*$/im.test(sql)) {
    observations.push({
      title: 'Missing Error Handling Detected',
      evidence: 'No TRY...CATCH blocks found.',
      why: 'Lack of error handling makes debugging and reliability difficult.',
      recommendation: 'Add TRY...CATCH blocks and proper error logging.'
    });
  }
  // 4. Magic Values
  if (/\b(\d{2,}|\'[^']+\')\b/.test(sql)) {
    observations.push({
      title: 'Magic Values Detected',
      evidence: 'Found hardcoded numbers or strings in code.',
      why: 'Magic values make code hard to understand and maintain.',
      recommendation: 'Replace with named constants or parameters.'
    });
  }
  // 5. NOLOCK usage
  if (/WITH\s*\(NOLOCK\)/i.test(sql)) {
    observations.push({
      title: 'NOLOCK Usage Detected',
      evidence: 'Found WITH (NOLOCK) in queries.',
      why: 'NOLOCK can cause dirty reads and data inconsistency.',
      recommendation: 'Use proper isolation levels instead.'
    });
  }
  // 6. Cursor usage
  if (/CURSOR/i.test(sql)) {
    observations.push({
      title: 'Cursor Implementation Detected',
      evidence: 'Found CURSOR usage.',
      why: 'Cursors are slow and create performance bottlenecks.',
      recommendation: 'Replace with set-based operations.'
    });
  }
  // 7. Unsafe DELETE
  if (/DELETE\s+FROM\s+\w+\s*;?$/im.test(sql)) {
    observations.push({
      title: 'Unsafe DELETE Operations Detected',
      evidence: 'DELETE statement without WHERE clause.',
      why: 'Can cause accidental full-table deletion.',
      recommendation: 'Always use WHERE clause in DELETE statements.'
    });
  }
  // 8. GOTO usage
  if (/\bGOTO\b/i.test(sql)) {
    observations.push({
      title: 'GOTO Statements Detected',
      evidence: 'Found GOTO in code.',
      why: 'GOTO leads to unstructured, hard-to-maintain code.',
      recommendation: 'Use structured control flow instead.'
    });
  }
  return observations;
}
// ErrorBoundary to catch React rendering errors
type ErrorBoundaryProps = { children: React.ReactNode };
type ErrorBoundaryState = { hasError: boolean; errorInfo: any };
class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, errorInfo: null };
  }
  static getDerivedStateFromError(error: any) {
    return { hasError: true, errorInfo: error };
  }
  componentDidCatch(error: any, errorInfo: any) {
    console.error('[ErrorBoundary] Caught error:', error, errorInfo);
  }
  render() {
    if (this.state.hasError) {
      return <div style={{ color: 'red', padding: 16 }}><b>Rendering error occurred in static analysis results.</b><br />Check console for details.</div>;
    }
    return this.props.children;
  }
}

function StaticAnalysisResults(props: any) {
  const { clientCode } = useParams<{ clientCode?: string }>();
  const clientKey = (clientCode || '').toUpperCase();
  const [selectedObservation, setSelectedObservation] = React.useState<number | null>(null);
  const codePreviewRef = React.useRef<HTMLDivElement>(null);
  
  // Try to load last uploaded SQL static analysis for this client
  let lastAnalysis: any = null;
  try {
    lastAnalysis = JSON.parse(localStorage.getItem(`last_sql_static_analysis_${clientKey}`) || 'null');
  } catch {}

  const fileName = lastAnalysis?.name || lastAnalysis?.fileName || '';
  const fileContent = lastAnalysis?.content || '';
  const observations = lastAnalysis?.observations || lastAnalysis?.data?.observations || [];

  // Function to get search patterns for highlighting based on observation type
  const getHighlightPatterns = (obs: any): string[] => {
    const patterns: string[] = [];
    
    if (obs.title.includes('Parameter Bloating')) {
      patterns.push('CREATE\\s+PROCEDURE[\\s\\S]+?\\([^)]*\\)');
    }
    if (obs.title.includes('Missing Error Handling')) {
      patterns.push('TRY', 'CATCH', 'BEGIN TRY', 'END TRY', 'BEGIN CATCH', 'END CATCH');
    }
    if (obs.title.includes('Magic Values')) {
      patterns.push('\\b\\d{2,}\\b', "'[^']*'", '"[^"]*"');
    }
    if (obs.title.includes('NOLOCK')) {
      patterns.push('WITH\\s*\\(NOLOCK\\)', 'NOLOCK');
    }
    if (obs.title.includes('Cursor')) {
      patterns.push('CURSOR', 'DECLARE.*CURSOR', 'OPEN\\s+\\w+', 'FETCH', 'CLOSE\\s+\\w+', 'DEALLOCATE');
    }
    if (obs.title.includes('DELETE')) {
      patterns.push('DELETE\\s+FROM\\s+\\w+\\s*;?$');
    }
    if (obs.title.includes('GOTO')) {
      patterns.push('\\bGOTO\\b', 'GOTO\\s+\\w+');
    }
    
    return patterns;
  };

  // Function to highlight code and scroll to first match
  const highlightCode = (code: string, obs: any): string => {
    if (!obs) return code;
    
    const patterns = getHighlightPatterns(obs);
    let highlightedCode = code;
    let firstMatchFound = false;
    
    patterns.forEach(pattern => {
      const regex = new RegExp(pattern, 'gi');
      highlightedCode = highlightedCode.replace(regex, (match) => {
        const id = !firstMatchFound ? 'first-evidence' : '';
        firstMatchFound = true;
        return `<mark id="${id}" style="background-color: #ffeb3b; color: #000; padding: 2px 4px; border-radius: 3px;">${match}</mark>`;
      });
    });
    
    return highlightedCode;
  };

  // Function to scroll to first evidence
  const scrollToFirstEvidence = () => {
    setTimeout(() => {
      const firstEvidence = codePreviewRef.current?.querySelector('#first-evidence');
      if (firstEvidence) {
        firstEvidence.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'center' 
        });
      }
    }, 100);
  };

  // Handle observation click
  const handleObservationClick = (idx: number) => {
    if (selectedObservation === idx) {
      setSelectedObservation(null);
    } else {
      setSelectedObservation(idx);
      scrollToFirstEvidence();
    }
  };

  // Basic summary info if no observations
  let summaryInfo: string | null = null;
  if (fileContent && observations.length === 0) {
    const lines = fileContent.split(/\r?\n/);
    const totalLines = lines.length;
    // Count code lines: not blank, not starting with --, not only whitespace
    const codeLines = lines.filter((l: string) => l.trim() && !/^\s*--/.test(l)).length;
    let paramCount = 0;
    const paramMatch = fileContent.match(/CREATE\s+PROCEDURE[\s\S]+?\(([^)]*)\)/i);
    if (paramMatch) {
      paramCount = paramMatch[1].split(',').length;
    }
    summaryInfo = `Total lines: ${totalLines}, Code lines: ${codeLines}, Parameters: ${paramCount}. No critical issues detected.`;
  }

  // Upload analysis result to Supabase Storage as JSON
  useEffect(() => {
    async function uploadAnalysisToSupabase() {
      if (!fileName || !fileContent) return;
      // Prepare analysis JSON
      const analysisData = {
        fileName,
        client: clientKey,
        analyzedAt: new Date().toISOString(),
        summary: summaryInfo,
        observations,
        filePreview: fileContent.slice(0, 1000) // limit preview for storage
      };
      const blob = new Blob([JSON.stringify(analysisData, null, 2)], { type: 'application/json' });
      const supaPath = `static-analysis/${clientKey}/${fileName.replace(/\.[^.]+$/, '')}_analysis_${Date.now()}.json`;
      // Only upload if not already uploaded for this file (avoid duplicates)
      if (window.__lastSupabaseUpload !== supaPath) {
        await supabase.storage.from('analysis-results').upload(supaPath, blob, { upsert: true });
        window.__lastSupabaseUpload = supaPath;
      }
    }
    uploadAnalysisToSupabase();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileName, fileContent, clientKey, summaryInfo, JSON.stringify(observations)]);

  return (
    <div style={{ padding: 32, color: 'gray' }}>
      <h2 style={{ fontWeight: 600, fontSize: 20, marginBottom: 16 }}>Static Analysis Results</h2>

      {fileName && (
        <div style={{ marginBottom: 16, color: 'black' }}>
          Selected file: <b>{fileName}</b>
        </div>
      )}
      {!fileName && (
        <div style={{ margin: '40px 0', color: '#aaa', fontSize: 16, textAlign: 'center' }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>🗂️</div>
          <div>
            No file selected.<br/>
            Use the Upload button in the top navbar to begin static analysis.
          </div>
        </div>
      )}
      
      {fileContent && (
        <div style={{ display: 'flex', gap: 24, minHeight: '800px' }}>
          {/* Left Pane - File Preview */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 500, marginBottom: 8, color: 'black' }}>File Preview:</div>
            <div 
              ref={codePreviewRef}
              style={{ 
                background: '#f5f5f5', 
                color: '#222', 
                padding: 16, 
                borderRadius: 8, 
                height: '780px', 
                overflow: 'auto', 
                fontSize: 13,
                lineHeight: 1.4,
                border: '1px solid #ddd',
                fontFamily: 'Monaco, Consolas, "Courier New", monospace',
                whiteSpace: 'pre-wrap'
              }}
              dangerouslySetInnerHTML={{
                __html: selectedObservation !== null 
                  ? highlightCode(fileContent, observations[selectedObservation])
                  : fileContent.replace(/</g, '&lt;').replace(/>/g, '&gt;')
              }}
            />
          </div>
          
          {/* Right Pane - Observations */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 500, marginBottom: 8, color: 'black' }}>
              Analysis Results ({observations.length} observations)
            </div>
            <div style={{ 
              height: '780px', 
              overflow: 'auto', 
              padding: 16,
              background: '#fafafa',
              borderRadius: 8,
              border: '1px solid #ddd'
            }}>
              {observations.length > 0 ? (
                observations.map((obs: any, idx: number) => (
                  <div 
                    key={idx} 
                    onClick={() => handleObservationClick(idx)}
                    style={{ 
                      background: selectedObservation === idx ? '#fff3cd' : '#fff', 
                      color: '#222', 
                      border: selectedObservation === idx ? '2px solid #ffc107' : '1px solid #eee', 
                      borderRadius: 8, 
                      marginBottom: 16, 
                      padding: 16, 
                      boxShadow: selectedObservation === idx ? '0 4px 8px rgba(255,193,7,0.3)' : '0 2px 4px rgba(0,0,0,0.1)',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    <div style={{ 
                      fontWeight: 600, 
                      fontSize: 15, 
                      marginBottom: 8, 
                      color: '#d73027',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between'
                    }}>
                      {obs.title}
                      <span style={{ 
                        fontSize: 12, 
                        color: '#2166ac', 
                        fontWeight: 500,
                        textDecoration: 'underline'
                      }}>
                        {selectedObservation === idx ? 'Hide highlights' : 'Go to code →'}
                      </span>
                    </div>
                    <div style={{ marginBottom: 6, fontSize: 13 }}>
                      <b style={{ color: '#666' }}>Evidence:</b> {obs.evidence}
                    </div>
                    <div style={{ marginBottom: 6, fontSize: 13 }}>
                      <b style={{ color: '#666' }}>Impact:</b> {obs.why}
                    </div>
                    <div style={{ fontSize: 13 }}>
                      <b style={{ color: '#2166ac' }}>Recommendation:</b> {obs.recommendation}
                    </div>
                  </div>
                ))
              ) : (
                <div style={{ textAlign: 'center', color: '#666', marginTop: 100 }}>
                  <div style={{ fontSize: 32, marginBottom: 12 }}>✅</div>
                  <div style={{ fontSize: 16 }}>No critical issues detected</div>
                  {summaryInfo && (
                    <div style={{ marginTop: 16, fontSize: 14, color: '#888' }}>
                      {summaryInfo}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      
      {summaryInfo && !fileContent && (
        <div style={{ marginTop: 32, color: '#444', fontSize: 16 }}>{summaryInfo}</div>
      )}
      <div style={{ marginTop: 24, color: '#888' }}>
        {fileName ? (observations.length === 0 && !summaryInfo ? 'No major issues detected.' : 'Analysis complete.') : 'Please select a .sql file to analyze.'}
      </div>
    </div>
  );
}

const StaticAnalysisResultsWithBoundary = (props: any) => (
  <ErrorBoundary>
    <StaticAnalysisResults {...props} />
  </ErrorBoundary>
);
// --- End Inlined StaticAnalysisResultsWithBoundary ---

// Generic static analysis page for any client
const StaticAnalysisPage: React.FC = () => {
  // Get clientCode from route params (wouter)
  const params = useParams<{ clientCode?: string }>();
  const clientCode = params.clientCode ? params.clientCode.toUpperCase() : 'Client';
  return (
    <div style={{ minHeight: '100vh', background: '#f9f9f9' }}>
      <div style={{ width: '100%', padding: 32 }}>
        <h1 style={{ fontSize: 28, fontWeight: 700, marginBottom: 24 }}>{clientCode} Static Analysis</h1>
        <StaticAnalysisResultsWithBoundary />
      </div>
    </div>
  );
};

export default StaticAnalysisPage;
